// POSCart - Cart display, item controls, and checkout orchestrator for POS
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ShoppingCart, Plus, Minus, X, Trash2, MessageSquare, Truck, Store } from "lucide-react";
import { POSCartDeliveryForm } from "./POSCartDeliveryForm";
import { POSCartCounterFooter } from "./POSCartCounterFooter";

export function POSCart({
  cart,
  subtotal,
  total,
  discountAmount = 0,
  onDiscountChange,
  itemCount,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onCheckout,
  onUpdateItemNotes,
  showPaymentButtons = true,
  isTableOrder = false,
  processing = false,
  orderNotes = "",
  onNotesChange,
  requireCustomer = false,
  customerName = "",
  onCustomerNameChange,
  customerPhone = "",
  onCustomerPhoneChange,
  extraPaymentMethod = "",
  extraPaymentMethods = [],
  // Delivery mode
  deliveryMode = false,
  onDeliveryModeChange,
  deliveryCustomerName = "",
  onDeliveryCustomerNameChange,
  deliveryCustomerPhone = "",
  onDeliveryCustomerPhoneChange,
  deliveryAddress = "",
  onDeliveryAddressChange,
  deliveryNotes = "",
  onDeliveryNotesChange,
  deliveryPaymentMethod = "card",
  onDeliveryPaymentMethodChange,
  onDeliveryCheckout,
  onDeliveryPhoneLookup,
}) {
  const isEmpty = cart.length === 0;
  const [openNoteKey, setOpenNoteKey] = useState(null);

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200">
      {/* Header */}
      <div className="bg-gray-50 border-b">
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-emerald-600" />
            <span className="font-bold text-gray-800">Pedido</span>
            {itemCount > 0 && (
              <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {itemCount}
              </span>
            )}
          </div>
          {!isEmpty && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8"
              onClick={onClearCart}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          )}
        </div>
        {/* Mode tabs */}
        {!isTableOrder && showPaymentButtons && onDeliveryModeChange && (
          <div className="flex border-t">
            <button
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold transition-colors ${!deliveryMode ? "bg-emerald-500 text-white" : "text-gray-500 hover:bg-gray-100"}`}
              onClick={() => onDeliveryModeChange(false)}
            >
              <Store className="w-3.5 h-3.5" /> Balcão
            </button>
            <button
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold transition-colors ${deliveryMode ? "bg-blue-500 text-white" : "text-gray-500 hover:bg-gray-100"}`}
              onClick={() => onDeliveryModeChange(true)}
            >
              <Truck className="w-3.5 h-3.5" /> Delivery
            </button>
          </div>
        )}
      </div>

      {/* Lista de itens */}
      <ScrollArea className="flex-1">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full py-12 text-gray-400">
            <ShoppingCart className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Carrito vacío</p>
            <p className="text-xs mt-1">Seleccione productos para agregar</p>
          </div>
        ) : (
          <div className="divide-y">
            {cart.map((item, idx) => {
              const itemKey = `${item.product_id}-${item.modifier_key || idx}`;
              const noteOpen = openNoteKey === itemKey;
              return (
                <div
                  key={itemKey}
                  className="px-3 py-2.5 hover:bg-gray-50 transition"
                  data-testid={`cart-item-${item.product_id?.slice(-6)}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.product_name}</p>
                      {item.modifier_description && (
                        <p className="text-xs text-emerald-600 truncate mt-0.5">{item.modifier_description}</p>
                      )}
                      {item.notes && !noteOpen && (
                        <p className="text-xs text-amber-600 mt-0.5 truncate">📝 {item.notes}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {item.unit_price.toFixed(2)}€ c/u
                        {item.extra_price > 0 && (
                          <span className="text-emerald-500 ml-1">(+{item.extra_price.toFixed(2)}€)</span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => setOpenNoteKey(noteOpen ? null : itemKey)}
                      className={`w-7 h-7 rounded-lg flex items-center justify-center transition ${item.notes ? "text-amber-500 bg-amber-50" : "text-gray-300 hover:text-amber-400 hover:bg-amber-50"}`}
                      title="Observação do item"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onUpdateQuantity(item.product_id, -1, item.modifier_key)}
                        className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-8 text-center text-sm font-bold text-gray-800">{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity(item.product_id, 1, item.modifier_key)}
                        className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-emerald-50 hover:text-emerald-500 flex items-center justify-center transition"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="text-right min-w-[60px]">
                      <p className="text-sm font-bold text-emerald-600">
                        {(item.quantity * item.unit_price).toFixed(2)}€
                      </p>
                    </div>
                    <button
                      onClick={() => onRemoveItem(item.product_id, item.modifier_key)}
                      className="text-gray-300 hover:text-red-400 transition p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {noteOpen && (
                    <div className="mt-1.5">
                      <input
                        autoFocus
                        type="text"
                        value={item.notes || ""}
                        onChange={e => onUpdateItemNotes?.(item.product_id, item.modifier_key, e.target.value)}
                        onKeyDown={e => e.key === "Enter" && setOpenNoteKey(null)}
                        placeholder={`Obs: ${item.product_name} (ex: sem queijo)`}
                        className="w-full text-xs rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400 placeholder:text-amber-300"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Footer com totais e ações */}
      {!isEmpty && (
        <div className="border-t bg-gray-50 p-3 space-y-3">
          {deliveryMode ? (
            <POSCartDeliveryForm
              deliveryCustomerName={deliveryCustomerName}
              onDeliveryCustomerNameChange={onDeliveryCustomerNameChange}
              deliveryCustomerPhone={deliveryCustomerPhone}
              onDeliveryCustomerPhoneChange={onDeliveryCustomerPhoneChange}
              deliveryAddress={deliveryAddress}
              onDeliveryAddressChange={onDeliveryAddressChange}
              orderNotes={orderNotes}
              onNotesChange={onNotesChange}
              deliveryNotes={deliveryNotes}
              onDeliveryNotesChange={onDeliveryNotesChange}
              deliveryPaymentMethod={deliveryPaymentMethod}
              onDeliveryPaymentMethodChange={onDeliveryPaymentMethodChange}
              total={total}
              subtotal={subtotal}
              processing={processing}
              onDeliveryCheckout={onDeliveryCheckout}
              onDeliveryPhoneLookup={onDeliveryPhoneLookup}
            />
          ) : (
            <POSCartCounterFooter
              orderNotes={orderNotes}
              onNotesChange={onNotesChange}
              subtotal={subtotal}
              total={total}
              discountAmount={discountAmount}
              onDiscountChange={onDiscountChange}
              requireCustomer={requireCustomer}
              customerName={customerName}
              onCustomerNameChange={onCustomerNameChange}
              customerPhone={customerPhone}
              onCustomerPhoneChange={onCustomerPhoneChange}
              showPaymentButtons={showPaymentButtons}
              isTableOrder={isTableOrder}
              processing={processing}
              onCheckout={onCheckout}
              extraPaymentMethods={extraPaymentMethods}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * POSCartMobile - Versão mobile do carrinho (bottom sheet)
 */
export function POSCartMobile({
  cart,
  subtotal,
  itemCount,
  isOpen,
  onToggle,
  children,
}) {
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-4 left-4 right-4 bg-emerald-500 text-white rounded-xl py-4 px-6 shadow-xl flex items-center justify-between z-50"
        data-testid="mobile-cart-button"
      >
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-6 h-6" />
          <span className="font-bold">{itemCount} itens</span>
        </div>
        <span className="font-bold text-lg">{subtotal.toFixed(2)}€</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50" onMouseDown={onToggle} onTouchStart={onToggle}>
      <div
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto"
        onMouseDown={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-center py-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>
        {children}
      </div>
    </div>
  );
}
